import React from 'react';
import { useEditorActions } from '../../store/editorStore.jsx';

/** Convert a plain YouTube/Vimeo URL to an embeddable iframe URL. */
function toEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  if (/embed|player/i.test(url)) return url;
  return null;
}

export function ImageCanvas({ block }) {
  const { updateAttrs } = useEditorActions();
  const { url = '', alt = '', isVideo = false, caption = '' } = block.attrs;

  const embedUrl = isVideo ? toEmbedUrl(url) : null;

  return (
    <div className="editor-image-block">
      {isVideo ? (
        <div
          className="border rounded d-flex align-items-center justify-content-center"
          style={{ aspectRatio: '16/9', minHeight: 180, background: '#0d1117', overflow: 'hidden' }}
        >
          {url && embedUrl ? (
            <iframe
              src={embedUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video preview"
            />
          ) : (
            <div className="text-center text-light p-3">
              <div style={{ fontSize: '2.5rem' }}>🎬</div>
              <div className="small mt-1">
                {url
                  ? "Direct video files won't preview here."
                  : 'Enter a YouTube or Vimeo URL below.'}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="border rounded d-flex align-items-center justify-content-center"
          style={{ minHeight: 120, background: '#f8f9fa' }}
        >
          {url ? (
            <img
              src={url}
              alt={alt}
              style={{ maxWidth: '100%', maxHeight: 350, objectFit: 'contain', padding: '0.5rem' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="text-center text-muted p-3">
              <div style={{ fontSize: '2.5rem' }}>🖼️</div>
              <div className="small mt-1">Enter an image URL below.</div>
            </div>
          )}
        </div>
      )}

      {caption && (
        <p className="text-center text-muted small mt-1 mb-0">{caption}</p>
      )}
    </div>
  );
}

export function ImageInspector({ block }) {
  const { updateAttrs } = useEditorActions();
  const { url = '', alt = '', caption = '', isVideo = false } = block.attrs;

  return (
    <div className="p-3">
      <div className="mb-3">
        <div className="d-flex gap-2 mb-2">
          <button
            type="button"
            className={`btn btn-sm ${!isVideo ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => updateAttrs(block.id, { isVideo: false })}
          >🖼️ Image</button>
          <button
            type="button"
            className={`btn btn-sm ${isVideo ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => updateAttrs(block.id, { isVideo: true })}
          >🎬 Video</button>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold small">
          {isVideo ? 'Video URL (YouTube / Vimeo)' : 'Image URL'}
        </label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={url}
          onChange={(e) => updateAttrs(block.id, { url: e.target.value })}
          placeholder={isVideo ? 'https://www.youtube.com/watch?v=…' : 'https://example.com/image.png'}
        />
      </div>

      {!isVideo && (
        <div className="mb-3">
          <label className="form-label fw-semibold small">Alt text</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={alt}
            onChange={(e) => updateAttrs(block.id, { alt: e.target.value })}
            placeholder="Describe the image"
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fw-semibold small">Caption</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={caption}
          onChange={(e) => updateAttrs(block.id, { caption: e.target.value })}
          placeholder="Optional caption"
        />
      </div>
    </div>
  );
}
